import { FC, useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from 'primereact/button';
import FormInputText from '../../components/form/FormInputText';
import FormInputNumber from '../../components/form/FormInputNumber';
import FormDropdown from '../../components/form/FormDropdown';
import FormCalendar from '../../components/form/FormCalendar';
import { OverrideSettingViewModel, FieldMetadata, MetadataContextType, DataType } from '../../models/waterfall';
import { EnumValueData } from '../../models/enum';
import { getFieldMetadata } from '../../services/expressionService';
import { getEnumValues } from '../../services/commonService';
import { getAllCustomFields } from '../../services/configurationService';
import { mergeCustomFieldsWithMetadata } from '../../utils/custom-field-helper';
import { filterFieldsByVendor } from './stepUtils';
import styles from './SimpleExpressionForm.module.css';

interface OverrideSettingFormProps {
  initialValue?: OverrideSettingViewModel;
  onChange?: (setting: OverrideSettingViewModel | null) => void;
  onDelete?: () => void;
  showDelete?: boolean;
  vendorName?: string | null;
}

interface FormValues {
  fieldName: string | null;
  value: string | number | null;
}

export const OverrideSettingForm: FC<OverrideSettingFormProps> = ({
  initialValue,
  onChange,
  onDelete,
  showDelete = true,
  vendorName,
}) => {
  const [fieldMetadata, setFieldMetadata] = useState<FieldMetadata[]>([]);
  const [filteredFieldMetadata, setFilteredFieldMetadata] = useState<FieldMetadata[]>([]);
  const [enumValuesCache, setEnumValuesCache] = useState<Record<string, { label: string; value: string }[]>>({});
  const [loadingEnums, setLoadingEnums] = useState<Set<string>>(new Set());
  const isInitialMount = useRef(true);

  const {
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      fieldName: initialValue?.fieldName || null,
      value: initialValue?.value || null,
    },
  });

  const formValues = watch();
  const selectedFieldName = watch('fieldName');
  const currentValue = formValues.value;

  useEffect(() => {
    reset({
      fieldName: initialValue?.fieldName || null,
      value: initialValue?.value || null,
    });
    isInitialMount.current = true;
  }, [initialValue, reset]);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [fields, customFields] = await Promise.all([
          getFieldMetadata(MetadataContextType.WaterfallRequestValueTarget),
          getAllCustomFields(),
        ]);
        const mergedFields = mergeCustomFieldsWithMetadata(fields, customFields);
        setFieldMetadata(mergedFields);
      } catch (error) {
        console.error('Error fetching field metadata:', error);
      }
    };
    fetchMetadata();
  }, []);

  useEffect(() => {
    if (!fieldMetadata.length) {
      setFilteredFieldMetadata([]);
      return;
    }

    const filtered = filterFieldsByVendor(fieldMetadata, vendorName);
    setFilteredFieldMetadata(filtered);
  }, [fieldMetadata, vendorName]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    if (selectedFieldName) {
      setValue('value', null);
    }
  }, [selectedFieldName, setValue]);

  useEffect(() => {
    if (onChange) {
      const fieldNameStr = formValues.fieldName;
      const valueStr = formValues.value;
      if (fieldNameStr && valueStr != null && String(valueStr).trim() !== '') {
        onChange({
          fieldName: fieldNameStr,
          value: String(valueStr),
        });
      } else {
        onChange(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.fieldName, formValues.value]);

  const fieldOptions = filteredFieldMetadata
    .map((field) => ({
      label: field.fieldName,
      value: field.fieldName,
    }))
    .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));

  const getFieldDataType = (fieldName: string | null): DataType | null => {
    if (!fieldName) return null;
    const field = fieldMetadata.find((f) => f.fieldName === fieldName);
    return field?.dataType || null;
  };

  const getFieldEnumType = (fieldName: string | null): string | null => {
    if (!fieldName) return null;
    const field = fieldMetadata.find((f) => f.fieldName === fieldName);
    return field?.enumType || null;
  };

  const shouldShowDropdown = (fieldName: string | null): boolean => {
    const dataType = getFieldDataType(fieldName);
    return dataType === DataType.TrueFalse || dataType === DataType.Enumeration;
  };

  const isLoadingEnumForField = (fieldName: string | null): boolean => {
    if (!fieldName) return false;
    return loadingEnums.has(fieldName);
  };

  const getValueOptions = (fieldName: string | null): { label: string; value: string }[] => {
    if (!fieldName) return [];
    const dataType = getFieldDataType(fieldName);

    if (dataType === DataType.TrueFalse) {
      return [
        { label: 'False', value: 'FALSE' },
        { label: 'True', value: 'TRUE' },
      ];
    }

    if (dataType === DataType.Enumeration) {
      const enumType = getFieldEnumType(fieldName);
      if (!enumType) return [];

      if (enumValuesCache[enumType]) {
        return enumValuesCache[enumType];
      }

      if (!loadingEnums.has(fieldName)) {
        setLoadingEnums((prev) => new Set(prev).add(fieldName));
        getEnumValues(enumType)
          .then((enumValues: EnumValueData[]) => {
            const options = enumValues.map((ev) => ({
                label: ev.displayName || ev.value || '',
                value: ev.value || '',
              }));
            setEnumValuesCache((prev) => ({
              ...prev,
              [enumType]: options,
            }));
            setLoadingEnums((prev) => {
              const newSet = new Set(prev);
              newSet.delete(fieldName);
              return newSet;
            });
          })
          .catch((error) => {
            console.error(`Error fetching enum values for ${enumType}:`, error);
            setLoadingEnums((prev) => {
              const newSet = new Set(prev);
              newSet.delete(fieldName);
              return newSet;
            });
          });
      }
      return [];
    }

    return [];
  };

  const isFeeAmountField = (fieldName: string | null): boolean => {
    if (!fieldName) return false;
    return fieldName.toLowerCase().includes('feeamount');
  };

  const isPercentageField = (fieldName: string | null): boolean => {
    if (!fieldName) return false;
    return fieldName.toLowerCase().includes('percentage');
  };

  return (
    <div className={`p-fluid p-input-filled flex gap-2 text-sm w-full ${styles.form}`}>
      <div className="min-w-42 flex-1" style={{ maxWidth: '300px' }}>
        <FormDropdown
          control={control}
          errors={errors}
          field="fieldName"
          label=""
          options={fieldOptions}
          optionLabel="label"
          optionValue="value"
          placeholder="Select property"
          className="mb-0"
          showFilter
          appendTo={document.body}
        />
      </div>

      <div className="flex items-center text-gray-400 font-bold" style={{ padding: '0 0.25rem' }}>
        =
      </div>

      <div className="min-w-42 flex-1" style={{ maxWidth: '300px' }}>
        {shouldShowDropdown(selectedFieldName) ? (
          <FormDropdown
            control={control}
            errors={errors}
            field="value"
            label=""
            options={getValueOptions(selectedFieldName)}
            optionLabel="label"
            optionValue="value"
            placeholder={isLoadingEnumForField(selectedFieldName) ? 'Loading...' : 'Select value'}
            className="mb-0"
            disabled={isLoadingEnumForField(selectedFieldName)}
            showFilter
            appendTo={document.body}
          />
        ) : getFieldDataType(selectedFieldName) === DataType.Number ||
          getFieldDataType(selectedFieldName) === DataType.Integer ? (
          <FormInputNumber
            control={control}
            errors={errors}
            field="value"
            label=""
            placeholder="Enter value"
            className="mb-0"
            prefix={isFeeAmountField(selectedFieldName) ? '$ ' : undefined}
            suffix={isPercentageField(selectedFieldName) ? ' %' : undefined}
          />
        ) : getFieldDataType(selectedFieldName) === DataType.Date ||
          getFieldDataType(selectedFieldName) === DataType.DateTime ? (
          <FormCalendar
            control={control}
            errors={errors}
            field="value"
            label=""
            placeholder="Select date"
            className="mb-0"
            showTime={getFieldDataType(selectedFieldName) === DataType.DateTime}
          />
        ) : (
          <FormInputText
            control={control}
            errors={errors}
            field="value"
            label=""
            placeholder="Enter value"
            className="mb-0"
          />
        )}
      </div>

      {onDelete && showDelete && (
        <Button type="button" icon="pi pi-trash" className="p-button-sm p-button-text p-button-danger" onClick={onDelete} />
      )}
    </div>
  );
};

